// New / edit booking (F-RSV-N1). Name, phone (with the guest's history once
// it matches a customer), party, date + time, length, tables, source, notes.
// The server is the judge of conflicts: a clash comes back as
// table_already_reserved, a party bigger than the tables as
// party_exceeds_capacity — which the dialog turns into an explicit
// "Book anyway" override.
import { useEffect, useMemo, useState } from 'react'
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { AlertTriangle, History } from 'lucide-react'
import apiClient, { ApiClientError } from '@/api/client'
import { Button } from '@/components/ui/button'
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle } from '@/components/ui/dialog'
import { Input } from '@/components/ui/input'
import { Label } from '@/components/ui/label'
import { Textarea } from '@/components/ui/textarea'
import { DatePicker } from '@/components/ui/date-picker'
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select'
import { useToast } from '@/hooks/use-toast'
import { cn, formatDateHuman } from '@/lib/utils'
import type { DiningTable } from '@/types'
import {
  SOURCE_LABEL,
  STAFF_SOURCES,
  hhmmLabel,
  pktHHMM,
  pktInstant,
  pktIsoDate,
  timeOptions,
  type GuestHistory,
  type Reservation,
  type ReservationSource,
  type ReservationWrite,
} from '@/lib/reservations'

const TIMES = timeOptions(15)

function useDebounced<T>(value: T, ms: number): T {
  const [v, setV] = useState(value)
  useEffect(() => {
    const t = setTimeout(() => setV(value), ms)
    return () => clearTimeout(t)
  }, [value, ms])
  return v
}

export function GuestHistoryNote({ history }: { history: GuestHistory }) {
  return (
    <div className="flex items-start gap-2 rounded-lg border bg-muted/40 p-3 text-sm">
      <History className="mt-0.5 h-4 w-4 shrink-0 text-muted-foreground" aria-hidden />
      <div className="space-y-0.5">
        <p className="font-medium">{history.name ? `Returning guest: ${history.name}` : 'Known number'}</p>
        <p className="text-muted-foreground">
          {history.visit_count} {history.visit_count === 1 ? 'visit' : 'visits'}
          {history.last_visit_at ? ` · last visit ${formatDateHuman(history.last_visit_at)}` : ''}
          {` · ${history.reservation_count} ${history.reservation_count === 1 ? 'booking' : 'bookings'}`}
        </p>
        {history.no_show_count > 0 ? (
          <p className="font-medium text-destructive">
            {history.no_show_count} {history.no_show_count === 1 ? 'no-show' : 'no-shows'} before
          </p>
        ) : null}
      </div>
    </div>
  )
}

export function BookingDialog({
  open,
  onOpenChange,
  booking,
  defaultDate,
  defaultDuration,
  tables,
  onSaved,
}: {
  open: boolean
  onOpenChange: (o: boolean) => void
  booking: Reservation | null
  defaultDate: string
  defaultDuration: number
  tables: DiningTable[]
  onSaved?: (r: Reservation) => void
}) {
  const qc = useQueryClient()
  const { toast } = useToast()
  const [name, setName] = useState('')
  const [phone, setPhone] = useState('')
  const [party, setParty] = useState('2')
  const [date, setDate] = useState(defaultDate)
  const [time, setTime] = useState('20:00')
  const [duration, setDuration] = useState(String(defaultDuration))
  const [tableIds, setTableIds] = useState<string[]>([])
  const [source, setSource] = useState<ReservationSource>('phone')
  const [notes, setNotes] = useState('')
  const [formError, setFormError] = useState<string | null>(null)
  const [capacityWarn, setCapacityWarn] = useState<{ capacity: number; party: number } | null>(null)

  useEffect(() => {
    if (!open) return
    setFormError(null)
    setCapacityWarn(null)
    if (booking) {
      setName(booking.guest_name)
      setPhone(booking.guest_phone ?? '')
      setParty(String(booking.party_size))
      setDate(pktIsoDate(booking.starts_at))
      setTime(pktHHMM(booking.starts_at))
      setDuration(String(booking.duration_minutes))
      setTableIds(booking.tables.map((t) => t.id))
      setSource(booking.source)
      setNotes(booking.notes ?? '')
    } else {
      setName('')
      setPhone('')
      setParty('2')
      setDate(defaultDate)
      setTime('20:00')
      setDuration(String(defaultDuration))
      setTableIds([])
      setSource('phone')
      setNotes('')
    }
  }, [open, booking, defaultDate, defaultDuration])

  const debouncedPhone = useDebounced(phone.trim(), 400)
  const { data: history } = useQuery({
    queryKey: ['reservations', 'guest', debouncedPhone],
    queryFn: () => apiClient.lookupReservationGuest(debouncedPhone).then((r) => r.data ?? null),
    enabled: open && debouncedPhone.replace(/\D/g, '').length >= 10,
    staleTime: 30_000,
  })

  const sortedTables = useMemo(
    () => [...tables].sort((a, b) => String(a.table_number).localeCompare(String(b.table_number), undefined, { numeric: true })),
    [tables],
  )
  const partyN = Number(party)
  const seats = sortedTables.filter((t) => tableIds.includes(t.id)).reduce((s, t) => s + (t.seating_capacity ?? 0), 0)

  const save = useMutation({
    mutationFn: async (override: boolean) => {
      const body: ReservationWrite = {
        guest_name: name.trim(),
        guest_phone: phone.trim() || undefined,
        party_size: partyN,
        starts_at: pktInstant(date, time),
        duration_minutes: Number(duration) || defaultDuration,
        table_ids: tableIds,
        notes: notes.trim() || undefined,
        source,
        capacity_override: override,
      }
      const res = booking ? await apiClient.updateReservation(booking.id, body) : await apiClient.createReservation(body)
      if (!res.success || !res.data) throw new Error(res.message || 'Could not save the booking')
      return res.data
    },
    onSuccess: (r) => {
      qc.invalidateQueries({ queryKey: ['reservations'] })
      toast({ title: booking ? 'Booking updated' : 'Booking saved', description: `${r.guest_name} · ${r.party_size} guests` })
      onSaved?.(r)
      onOpenChange(false)
    },
    onError: (e: unknown) => {
      if (e instanceof ApiClientError && e.code === 'party_exceeds_capacity') {
        const p = (e.payload ?? {}) as { capacity?: number; party_size?: number }
        setCapacityWarn({ capacity: p.capacity ?? seats, party: p.party_size ?? partyN })
        setFormError(null)
        return
      }
      setCapacityWarn(null)
      setFormError(e instanceof Error ? e.message : 'Could not save the booking')
    },
  })

  const submit = (override = false) => {
    setFormError(null)
    if (!name.trim()) return setFormError("Enter the guest's name")
    if (!Number.isInteger(partyN) || partyN < 1 || partyN > 200) return setFormError('Party size must be 1 to 200')
    if (!date) return setFormError('Pick a date')
    save.mutate(override)
  }

  const toggleTable = (id: string) => {
    setCapacityWarn(null)
    setTableIds((prev) => (prev.includes(id) ? prev.filter((x) => x !== id) : [...prev, id]))
  }

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-h-[90vh] overflow-y-auto sm:max-w-2xl">
        <DialogHeader>
          <DialogTitle>{booking ? 'Edit booking' : 'New booking'}</DialogTitle>
          <DialogDescription>Times are Pakistan time. The table is held for the booking length.</DialogDescription>
        </DialogHeader>

        <div className="grid gap-4 sm:grid-cols-2">
          <div className="space-y-1.5">
            <Label htmlFor="resv-name">Guest name</Label>
            <Input id="resv-name" value={name} onChange={(e) => setName(e.target.value)} placeholder="Ahmed Khan" autoFocus />
          </div>
          <div className="space-y-1.5">
            <Label htmlFor="resv-phone">Phone</Label>
            <Input
              id="resv-phone"
              inputMode="tel"
              value={phone}
              onChange={(e) => setPhone(e.target.value)}
              placeholder="0301 2345678"
            />
          </div>
          {history ? (
            <div className="sm:col-span-2">
              <GuestHistoryNote history={history} />
            </div>
          ) : null}
          <div className="space-y-1.5">
            <Label htmlFor="resv-party">Guests</Label>
            <Input
              id="resv-party"
              type="number"
              inputMode="numeric"
              min={1}
              max={200}
              value={party}
              onChange={(e) => {
                setParty(e.target.value)
                setCapacityWarn(null)
              }}
            />
          </div>
          <div className="space-y-1.5">
            <Label>Source</Label>
            <Select value={source} onValueChange={(v) => setSource(v as ReservationSource)}>
              <SelectTrigger aria-label="Source">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                {(booking?.source === 'online' ? (['online', ...STAFF_SOURCES] as ReservationSource[]) : STAFF_SOURCES).map((s) => (
                  <SelectItem key={s} value={s}>
                    {SOURCE_LABEL[s]}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
          <div className="space-y-1.5">
            <Label>Date</Label>
            <DatePicker value={date} onChange={setDate} className="w-full" />
          </div>
          <div className="grid grid-cols-2 gap-3">
            <div className="space-y-1.5">
              <Label>Time</Label>
              <Select value={time} onValueChange={setTime}>
                <SelectTrigger aria-label="Time">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent className="max-h-72">
                  {(TIMES.includes(time) ? TIMES : [...TIMES, time].sort()).map((t) => (
                    <SelectItem key={t} value={t}>
                      {hhmmLabel(t)}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <div className="space-y-1.5">
              <Label htmlFor="resv-duration-input">Minutes</Label>
              <Input
                id="resv-duration-input"
                type="number"
                inputMode="numeric"
                min={15}
                max={720}
                step={15}
                value={duration}
                onChange={(e) => setDuration(e.target.value)}
              />
            </div>
          </div>

          <div className="space-y-1.5 sm:col-span-2">
            <div className="flex items-center justify-between">
              <Label>Tables</Label>
              <span className="text-xs text-muted-foreground">
                {tableIds.length === 0 ? 'No table yet — assign one now or when they arrive' : `${seats} seats selected`}
              </span>
            </div>
            <div className="flex flex-wrap gap-2">
              {sortedTables.map((t) => {
                const on = tableIds.includes(t.id)
                return (
                  <button
                    key={t.id}
                    type="button"
                    onClick={() => toggleTable(t.id)}
                    aria-pressed={on}
                    className={cn(
                      'rounded-lg border px-3 py-1.5 text-sm transition-colors',
                      on ? 'border-primary bg-primary text-primary-foreground' : 'bg-card hover:border-primary/50',
                    )}
                  >
                    <span className="font-medium">{t.table_number}</span>
                    <span className={cn('ml-1.5 text-xs', on ? 'text-primary-foreground/80' : 'text-muted-foreground')}>
                      {t.seating_capacity} seats
                    </span>
                  </button>
                )
              })}
            </div>
          </div>

          <div className="space-y-1.5 sm:col-span-2">
            <Label htmlFor="resv-notes">Notes</Label>
            <Textarea
              id="resv-notes"
              rows={2}
              value={notes}
              onChange={(e) => setNotes(e.target.value)}
              placeholder="Birthday, high chair, window seat…"
            />
          </div>
        </div>

        {capacityWarn ? (
          <div className="flex items-start gap-2 rounded-lg border border-warning-border bg-warning-surface p-3 text-sm text-warning-fg">
            <AlertTriangle className="mt-0.5 h-4 w-4 shrink-0" aria-hidden />
            <div className="flex-1">
              <p className="font-medium">
                {capacityWarn.party} guests, but the chosen tables seat {capacityWarn.capacity}.
              </p>
              <p>Add a table, or book anyway if you will bring extra chairs.</p>
            </div>
          </div>
        ) : null}
        {formError ? (
          <p role="alert" className="text-sm font-medium text-destructive">
            {formError}
          </p>
        ) : null}

        <DialogFooter>
          <Button variant="outline" onClick={() => onOpenChange(false)}>
            Close
          </Button>
          {capacityWarn ? (
            <Button variant="secondary" disabled={save.isPending} onClick={() => submit(true)}>
              Book anyway
            </Button>
          ) : null}
          <Button disabled={save.isPending} onClick={() => submit(false)}>
            {save.isPending ? 'Saving…' : booking ? 'Save changes' : 'Save booking'}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  )
}
